import { useCallback, useEffect, useRef, useState } from 'react';
import areaIntelService, {
  TrafficIncident,
  WeatherAlert,
} from '../../services/areaIntelService';

export type MapAlertViewport = {
  bounds: { north: number; south: number; east: number; west: number };
  zoom: number;
};

export function useMapAreaAlerts(
  enabled: boolean,
  getViewport: () => MapAlertViewport | null,
) {
  const [incidents, setIncidents] = useState<TrafficIncident[]>([]);
  const [weatherAlerts, setWeatherAlerts] = useState<WeatherAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  const getViewportRef = useRef(getViewport);
  getViewportRef.current = getViewport;

  const reload = useCallback(async () => {
    if (!enabled) return;
    const viewport = getViewportRef.current();
    if (!viewport) return;
    const id = ++requestId.current;
    setLoading(true);
    try {
      const response = await areaIntelService.getAreaAlerts(viewport.bounds, {
        includeWeather: true,
        zoom: viewport.zoom,
      });
      if (id !== requestId.current) return;
      setIncidents(response.incidents || []);
      setWeatherAlerts(response.weather_alerts || []);
    } catch (err) {
      console.error('[map] failed to load traffic incidents', err);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      requestId.current += 1;
      setIncidents((cur) => (cur.length ? [] : cur));
      setWeatherAlerts((cur) => (cur.length ? [] : cur));
      setLoading(false);
      return;
    }
    void reload();
  }, [enabled, reload]);

  return { incidents, weatherAlerts, loading, reload };
}
